/**
 * Upload.tsx — Ingest client data and run the matching pipeline.
 *
 * Rebuilt in final pass.
 *
 * Full flow preserved:
 *   1. Select an existing client or create a new one.
 *   2. Drag-and-drop or browse for a CSV/XLSX file → POST /api/upload.
 *   3. Review parsed columns + preview, map canonical fields → POST /api/upload/map.
 *   4. Backend kicks off pipeline; poll /api/pipeline/job/{id} every 3s.
 *   5. Show running / completed / failed states.
 *   6. "Upload another file" resets the whole flow.
 *
 * Canonical fields sent to backend unchanged: name, address, city, vat.
 * Display label for vat is now "Tax ID" for consistency with public pages.
 *
 * New:
 *   - Uses shared UI (Card, Button, Input, Select, FormField, Modal-style reset,
 *     Toast for notifications)
 *   - Proper warning banner when creating a new client name (typo prevention)
 */

import { useEffect, useState, useRef, DragEvent } from 'react';
import axios from 'axios';
import api from '../../api';
import { theme } from '../../theme';
import { captureEvent } from '../../analytics/posthog';
import {
  Card,
  Button,
  Input, Select,
  Icon,
  Tag,
  toast,
} from '../../components/ui';

/* ═══════════════ TYPES ═══════════════ */

const CANONICAL = ['name', 'address', 'city', 'vat'] as const;
type CanonicalField = typeof CANONICAL[number];

const CANONICAL_LABELS: Record<CanonicalField, string> = {
  name:    'Name',
  address: 'Address',
  city:    'City',
  vat:     'Tax ID',
};

interface UploadResult {
  upload_id: string;
  columns: string[];
  preview: string[][];
  rows: number;
  suggested_mapping: Record<string, string>;
}

interface JobStatus {
  job_id: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  progress: {
    current: number;
    total: number;
    percent: number;
    message: string | null;
  };
  result: PipelineResult[] | null;
  error: string | null;
  duration_seconds: number | null;
}

interface PipelineResult {
  client: string;
  deterministic_matched: number;
  fuzzy_confirmed: number;
  sent_to_review: number;
  new_outlets: number;
  registry_enriched: number;
  duration: number;
}

/* ═══════════════ PAGE ═══════════════ */

export default function Upload() {
  // Client selection
  const [existingClients, setExistingClients] = useState<string[]>([]);
  const [clientName, setClientName] = useState('');
  const [isNewClient, setIsNewClient] = useState(false);
  const [newClientName, setNewClientName] = useState('');

  // Upload stage
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState<UploadResult | null>(null);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [ingesting, setIngesting] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  // Pipeline job tracking
  const [activeJobId, setActiveJobId] = useState<string | null>(null);
  const [jobStatus, setJobStatus] = useState<JobStatus | null>(null);
  const [recordsIngested, setRecordsIngested] = useState(0);

  useEffect(() => {
    document.title = 'Upload & Map | Meridian Registry';
    return () => { document.title = 'Meridian Registry'; };
  }, []);

  // Load existing client names
  useEffect(() => {
    api.get<string[]>('/api/clients')
      .then(r => setExistingClients(r.data || []))
      .catch(() => {});
  }, []);

  const effectiveClientName = isNewClient ? newClientName.trim() : clientName;

  // Poll job status every 3s
  useEffect(() => {
    if (!activeJobId) return;
    let active = true;

    const poll = async () => {
      try {
        const res = await api.get<JobStatus>(`/api/pipeline/job/${activeJobId}`);
        if (!active) return;
        setJobStatus(res.data);
        if (res.data.status === 'completed' || res.data.status === 'failed') {
          clearInterval(interval);
        }
      } catch {
        /* ignore */
      }
    };

    poll();
    const interval = setInterval(poll, 3000);
    return () => { active = false; clearInterval(interval); };
  }, [activeJobId]);

  /* ─── File upload handler ─── */
  const handleFile = async (file: File) => {
    if (!effectiveClientName) {
      toast.error('Select or enter a client name before uploading');
      return;
    }
    setUploading(true);
    setUploadResult(null);
    setActiveJobId(null);
    setJobStatus(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('client_name', effectiveClientName);
      const res = await api.post<UploadResult>('/api/upload', fd);
      setUploadResult(res.data);

      // Pre-fill mapping from backend suggestions
      const suggested = res.data.suggested_mapping || {};
      const initial: Record<string, string> = {};
      for (const field of CANONICAL) {
        const src = Object.entries(suggested).find(([, v]) => v === field)?.[0] || '';
        initial[field] = src;
      }
      setMapping(initial);
      toast.success('File parsed', `${res.data.rows.toLocaleString()} rows ready to map`);
    } catch (err: unknown) {
      const msg = axios.isAxiosError(err) ? (err.response?.data?.detail || err.message) : 'Upload failed';
      toast.error('Upload failed', msg);
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files[0];
    if (f) handleFile(f);
  };
  const onBrowse = () => fileRef.current?.click();
  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) handleFile(f);
  };

  /* ─── Ingest + dispatch pipeline ─── */
  const ingestAndRun = async () => {
    if (!uploadResult) return;
    setIngesting(true);
    try {
      const res = await api.post('/api/upload/map', {
        upload_id: uploadResult.upload_id,
        mapping,
      });
      const d = res.data;
      setRecordsIngested(d.records_ingested || 0);
      setActiveJobId(d.job_id);
      captureEvent('pipeline_triggered', {
        client_name: effectiveClientName,
        records: d.records_ingested || 0,
      });

      // Refresh client list in case a new client was just created
      const cRes = await api.get<string[]>('/api/clients');
      setExistingClients(cRes.data || []);
    } catch (err: unknown) {
      const msg = axios.isAxiosError(err) ? (err.response?.data?.detail || err.message) : 'Ingest failed';
      toast.error('Ingest failed', msg);
    } finally {
      setIngesting(false);
    }
  };

  /* ─── Reset ─── */
  const resetAll = () => {
    setUploadResult(null);
    setMapping({});
    setActiveJobId(null);
    setJobStatus(null);
    setRecordsIngested(0);
    setIsNewClient(false);
    setNewClientName('');
  };

  /* ─── Summary math for completed run ─── */
  const results = jobStatus?.status === 'completed' && jobStatus.result
    ? (Array.isArray(jobStatus.result) ? jobStatus.result : [])
    : [];
  const totalMatched  = results.reduce((s, r) => s + r.deterministic_matched + r.fuzzy_confirmed, 0);
  const totalNew      = results.reduce((s, r) => s + r.new_outlets, 0);
  const totalReview   = results.reduce((s, r) => s + r.sent_to_review, 0);
  const totalEnriched = results.reduce((s, r) => s + r.registry_enriched, 0);

  /* ═══════════════ STAGE 1: PIPELINE RUNNING ═══════════════ */
  if (activeJobId && jobStatus && jobStatus.status !== 'completed' && jobStatus.status !== 'failed') {
    return (
      <div style={{ padding: 32 }}>
        <PageHeader />
        <Card style={{ maxWidth: 560, marginTop: 32 }}>
          <div style={{
            fontSize: 11, fontWeight: 600,
            color: theme.brand.cyan,
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
            marginBottom: 14,
          }}>Pipeline running</div>

          <div style={{
            height: 6,
            background: theme.surface.card,
            borderRadius: 3,
            overflow: 'hidden',
            marginBottom: 12,
          }}>
            <div style={{
              height: '100%',
              width: `${jobStatus.progress.percent}%`,
              background: theme.brand.cyan,
              borderRadius: 3,
              transition: 'width 300ms ease',
              boxShadow: `0 0 12px ${theme.brand.cyanGlow}`,
            }} />
          </div>

          <div style={{
            fontSize: 13,
            color: theme.text.secondary,
          }}>
            {jobStatus.progress.message || `${jobStatus.status}…`}
          </div>
        </Card>
      </div>
    );
  }

  /* ═══════════════ STAGE 2: PIPELINE COMPLETED ═══════════════ */
  if (activeJobId && jobStatus?.status === 'completed') {
    return (
      <div style={{ padding: 32 }}>
        <PageHeader />
        <Card
          variant="primary"
          style={{
            maxWidth: 600,
            marginTop: 32,
            borderColor: theme.semantic.successBorder,
            background: `linear-gradient(135deg, ${theme.surface.panel} 0%, ${theme.semantic.successSoft} 100%)`,
          }}
        >
          <div style={{
            display: 'flex', alignItems: 'center', gap: 12,
            marginBottom: 20,
          }}>
            <div style={{
              width: 36, height: 36, borderRadius: '50%',
              background: theme.semantic.successSoft,
              border: `1px solid ${theme.semantic.successBorder}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: theme.semantic.success,
              flexShrink: 0,
            }}>
              <Icon.Check size={18} />
            </div>
            <div>
              <div style={{
                fontSize: 17, fontWeight: 600,
                color: theme.text.primary,
              }}>Pipeline complete</div>
              <div style={{
                fontSize: 12, color: theme.text.tertiary,
                fontFamily: theme.font.data,
                marginTop: 2,
              }}>{jobStatus.duration_seconds}s</div>
            </div>
          </div>

          <div style={{
            color: theme.text.secondary,
            fontSize: 14,
            lineHeight: 1.8,
          }}>
            <div>
              <strong style={{ color: theme.text.primary, fontWeight: 600 }}>
                {recordsIngested.toLocaleString()}
              </strong>
              {' '}records ingested for{' '}
              <strong style={{ color: theme.brand.cyanLight, fontWeight: 600 }}>
                {effectiveClientName}
              </strong>
            </div>
            <div style={{
              paddingLeft: 16,
              borderLeft: `2px solid ${theme.borderColor.default}`,
              marginTop: 8,
              fontSize: 13,
            }}>
              <StatLine label="matched to registry" value={totalMatched} color={theme.semantic.success} />
              <StatLine label="new outlets added" value={totalNew} color={theme.brand.cyanLight} />
              <StatLine label="sent to review" value={totalReview} color={theme.semantic.warning} />
              <StatLine label="registry records enriched" value={totalEnriched} color={theme.text.secondary} />
            </div>
          </div>

          <div style={{
            fontSize: 12,
            color: theme.text.tertiary,
            marginTop: 20, marginBottom: 20,
          }}>
            Your dashboard has been updated.
          </div>

          <Button variant="primary" onClick={resetAll} leftIcon={<Icon.Upload size={14} />}>
            Upload another file
          </Button>
        </Card>
      </div>
    );
  }

  /* ═══════════════ STAGE 3: PIPELINE FAILED ═══════════════ */
  if (activeJobId && jobStatus?.status === 'failed') {
    return (
      <div style={{ padding: 32 }}>
        <PageHeader />
        <Card
          style={{
            maxWidth: 560,
            marginTop: 32,
            borderColor: theme.semantic.dangerBorder,
            background: theme.semantic.dangerSoft,
          }}
        >
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10,
            color: theme.semantic.danger,
            fontWeight: 600,
            fontSize: 15,
            marginBottom: 10,
          }}>
            <Icon.AlertTriangle size={18} />
            Pipeline failed
          </div>
          <div style={{
            color: theme.text.secondary,
            fontSize: 13,
            lineHeight: 1.6,
            marginBottom: 20,
          }}>{jobStatus.error || 'Unknown error occurred.'}</div>
          <Button variant="secondary" onClick={resetAll}>Try again</Button>
        </Card>
      </div>
    );
  }

  /* ═══════════════ STAGE 0: MAIN UPLOAD FORM ═══════════════ */
  return (
    <div style={{ padding: 32 }}>
      <PageHeader />

      {/* Client selector card */}
      <div style={{ maxWidth: 480, marginTop: 28 }}>
        <div style={{
          fontSize: 11, fontWeight: 600,
          color: theme.text.tertiary,
          textTransform: 'uppercase',
          letterSpacing: '0.1em',
          marginBottom: 10,
        }}>Client</div>

        {!isNewClient ? (
          <Select
            value={clientName}
            onChange={e => {
              if (e.target.value === '__new__') {
                setIsNewClient(true);
                setClientName('');
              } else {
                setClientName(e.target.value);
              }
            }}
          >
            <option value="">Select client…</option>
            {existingClients.map(c => <option key={c} value={c}>{c}</option>)}
            <option disabled>────────────</option>
            <option value="__new__">+ Add new client</option>
          </Select>
        ) : (
          <>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <div style={{ flex: 1 }}>
                <Input
                  value={newClientName}
                  onChange={e => setNewClientName(e.target.value)}
                  placeholder="Enter new client name…"
                  autoFocus
                />
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { setIsNewClient(false); setNewClientName(''); }}
              >Cancel</Button>
            </div>

            {newClientName.trim() && (
              <div style={{
                marginTop: 10,
                padding: '8px 12px',
                background: theme.semantic.warningSoft,
                border: `1px solid ${theme.semantic.warningBorder}`,
                borderRadius: 6,
                fontSize: 12,
                color: theme.semantic.warning,
                lineHeight: 1.5,
                display: 'flex', alignItems: 'flex-start', gap: 8,
              }}>
                <Icon.AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
                <span>
                  You are creating a new client:{' '}
                  <strong style={{ fontWeight: 700 }}>{newClientName.trim()}</strong>
                  . Double-check the name — a typo cannot be undone automatically.
                </span>
              </div>
            )}
          </>
        )}
      </div>

      {/* Drop zone */}
      <div
        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={onBrowse}
        style={{
          marginTop: 20,
          maxWidth: 480,
          padding: '32px 28px',
          background: dragOver ? theme.brand.cyanSoft : theme.surface.panel,
          border: `2px dashed ${dragOver ? theme.brand.cyan : theme.borderColor.default}`,
          borderRadius: 12,
          textAlign: 'center',
          cursor: effectiveClientName ? 'pointer' : 'not-allowed',
          transition: 'all 180ms ease',
          opacity: effectiveClientName ? 1 : 0.5,
          pointerEvents: effectiveClientName ? 'auto' : 'none',
        }}>
        <div style={{
          color: dragOver ? theme.brand.cyan : theme.text.tertiary,
          marginBottom: 10,
          display: 'flex',
          justifyContent: 'center',
        }}>
          <Icon.Upload size={28} />
        </div>
        <div style={{
          color: theme.text.primary,
          fontSize: 14,
          fontWeight: 500,
          marginBottom: 4,
        }}>
          {uploading
            ? 'Uploading…'
            : effectiveClientName
              ? 'Drag and drop or click to browse'
              : 'Select a client first'}
        </div>
        <div style={{
          color: theme.text.tertiary,
          fontSize: 12,
        }}>Limit 50MB per file · CSV, XLSX</div>
        <input
          ref={fileRef}
          type="file"
          accept=".csv,.xlsx"
          style={{ display: 'none' }}
          onChange={onFileChange}
        />
      </div>

      {/* Column mapping */}
      {uploadResult && (
        <div style={{ marginTop: 32 }}>
          <div style={{
            fontSize: 11, fontWeight: 600,
            color: theme.text.tertiary,
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
            marginBottom: 10,
          }}>Map columns</div>

          <div style={{
            color: theme.text.secondary,
            fontSize: 13,
            marginBottom: 16,
          }}>
            Loaded{' '}
            <strong style={{
              color: theme.text.primary,
              fontFamily: theme.font.data,
              fontWeight: 600,
            }}>{uploadResult.rows.toLocaleString()}</strong>
            {' '}rows,{' '}
            <strong style={{
              color: theme.text.primary,
              fontFamily: theme.font.data,
              fontWeight: 600,
            }}>{uploadResult.columns.length}</strong>
            {' '}columns for{' '}
            <strong style={{ color: theme.brand.cyanLight, fontWeight: 600 }}>
              {effectiveClientName}
            </strong>
            <span style={{ marginLeft: 10 }}>
              <Tag kind="neutral" size="sm">Preview</Tag>
            </span>
          </div>

          {/* Preview table */}
          <div style={{
            border: `1px solid ${theme.borderColor.default}`,
            borderRadius: 8,
            overflow: 'auto',
            marginBottom: 20,
            maxWidth: '100%',
            background: theme.surface.panel,
          }}>
            <table style={{
              borderCollapse: 'collapse',
              fontSize: 12,
              fontFamily: theme.font.data,
              whiteSpace: 'nowrap',
              width: '100%',
            }}>
              <thead>
                <tr style={{ background: theme.surface.card }}>
                  {uploadResult.columns.map(c => (
                    <th key={c} style={{
                      padding: '8px 12px',
                      color: theme.text.tertiary,
                      fontSize: 10, fontWeight: 600,
                      letterSpacing: '0.08em',
                      textAlign: 'left',
                      textTransform: 'uppercase',
                      borderBottom: `1px solid ${theme.borderColor.default}`,
                    }}>{c}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {uploadResult.preview.map((row, ri) => (
                  <tr key={ri} style={{
                    borderBottom: ri === uploadResult.preview.length - 1
                      ? 'none'
                      : `1px solid ${theme.borderColor.subtle}`,
                  }}>
                    {row.map((cell, ci) => (
                      <td key={ci} style={{
                        padding: '7px 12px',
                        color: theme.text.primary,
                        maxWidth: 200,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}>{String(cell)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mapping rows */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '140px 1fr',
            gap: '10px 14px',
            maxWidth: 480,
            alignItems: 'center',
            marginBottom: 20,
          }}>
            {CANONICAL.map(field => (
              <MappingRow
                key={field}
                label={CANONICAL_LABELS[field]}
                columns={uploadResult.columns}
                value={mapping[field] || ''}
                onChange={val => setMapping(prev => ({ ...prev, [field]: val }))}
              />
            ))}
          </div>

          <Button
            variant="primary"
            loading={ingesting}
            disabled={ingesting}
            onClick={ingestAndRun}
            leftIcon={<Icon.Zap size={14} />}
          >
            {ingesting ? 'Ingesting…' : 'Ingest & run pipeline'}
          </Button>
        </div>
      )}
    </div>
  );
}

/* ═══════════════ SUB-COMPONENTS ═══════════════ */

function PageHeader() {
  return (
    <div>
      <h1 style={{
        fontSize: 28, fontWeight: 600,
        color: theme.text.primary,
        margin: 0,
        letterSpacing: '-0.02em',
      }}>Upload & map</h1>
      <div aria-hidden style={{
        height: 3, width: 40,
        background: theme.brand.cyan,
        borderRadius: 2,
        boxShadow: `0 0 12px ${theme.brand.cyanGlow}`,
        marginTop: 6,
      }} />
      <div style={{
        fontSize: 11, fontWeight: 600,
        color: theme.text.tertiary,
        textTransform: 'uppercase',
        letterSpacing: '0.14em',
        marginTop: 8,
      }}>Ingest client data and run the matching pipeline</div>
    </div>
  );
}

function MappingRow({
  label, columns, value, onChange,
}: {
  label: string;
  columns: string[];
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <>
      <div style={{
        fontSize: 12, fontWeight: 600,
        color: theme.text.secondary,
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
      }}>{label}</div>
      <Select value={value} onChange={e => onChange(e.target.value)}>
        <option value="">(unmapped)</option>
        {columns.map(c => <option key={c} value={c}>{c}</option>)}
      </Select>
    </>
  );
}

function StatLine({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div>
      <strong style={{
        color,
        fontFamily: theme.font.data,
        fontWeight: 600,
        fontVariantNumeric: 'tabular-nums',
      }}>{value.toLocaleString()}</strong>
      {' '}
      <span style={{ color: theme.text.secondary }}>{label}</span>
    </div>
  );
}
